import { createSelector } from "reselect";

import { getCurrentDeploymentSaleIds } from "./currentSale";
import { selectVendorIdBySaleLotIdLookup } from "./indexes";
import {
  getBusinesses,
  getEContractAgreements,
  getEContractSpecialConditions,
  getEContracts,
  getSaleLots,
} from "./root";

export const getEContractAgreementById: (
  agreementId: string,
) => (state: State) => EContractAgreement | null = agreementId => state =>
  getEContractAgreements(state)[agreementId] || null;

export const selectEContractAgreementsInCurrentSaleList: (
  state: State,
) => EContractAgreement[] = createSelector(
  [getCurrentDeploymentSaleIds, getEContractAgreements],
  (
    deploymentSaleIds: number[],
    agreements: Record<string, EContractAgreement>,
  ) => {
    return Object.values(agreements).filter(a =>
      deploymentSaleIds.includes(a.deploymentSaleId),
    );
  },
);

export const selectEContractSpecialConditionsByAgreementId: (
  state: State,
) => Record<string, EContractSpecialCondition[]> = createSelector(
  [getEContractSpecialConditions, selectEContractAgreementsInCurrentSaleList],
  (
    conditions: Record<string, EContractSpecialCondition>,
    agreements: EContractAgreement[],
  ) => {
    const agreementIds = new Set(agreements.map(a => a.id));
    const result: Record<string, EContractSpecialCondition[]> = {};
    Object.values(conditions)
      .filter(condition => agreementIds.has(condition.agreementId))
      .forEach(condition => {
        if (!result[condition.agreementId]) {
          result[condition.agreementId] = [];
        }
        result[condition.agreementId].push(condition);
      });
    Object.values(result).forEach(list =>
      list.sort((a, b) =>
        // if there is no created value (before server response)
        // default to 9999 so it remains at the bottom of the list
        (a.created ?? "9999").localeCompare(b.created ?? "9999"),
      ),
    );
    return result;
  },
);

export const selectActiveEContractByEContractAgreementId: (
  state: State,
) => Record<string, EContract | undefined> = createSelector(
  [selectEContractAgreementsInCurrentSaleList, getEContracts],
  (agreements, eContracts: Record<string, EContract>) => {
    const result: Record<string, EContract | undefined> = {};
    agreements.forEach(agreement => {
      const active = agreement.econtractIds
        ?.map(id => eContracts[id])
        .filter((ec): ec is EContract => !!ec)
        .find(ec => !ec.voidedOn);
      result[agreement.id] = active;
    });
    return result;
  },
);

export const selectSaleLotIdsWithoutEContractAgreement: (
  state: State,
) => string[] = createSelector(
  [
    selectEContractAgreementsInCurrentSaleList,
    getSaleLots,
    selectVendorIdBySaleLotIdLookup,
  ],
  (agreements, saleLots, vendorIdBySaleLotId) => {
    const vendorAndBuyerPairs = new Set(
      agreements.map(a => `${a.vendorBusinessId}:${a.buyerBusinessId}`),
    );

    return Object.entries(saleLots as Record<string, SaleLot>)
      .filter(([lotId, lot]) => {
        const vendorId = vendorIdBySaleLotId[lotId];
        if (!lot.buyer_id || !vendorId) {
          return false;
        }
        return !vendorAndBuyerPairs.has(`${vendorId}:${lot.buyer_id}`);
      })
      .map(([lotId]) => lotId);
  },
);

export const getBuyerByEContractAgreementId: (
  agreementId: string,
) => (state: State) => Business | null = agreementId => state => {
  const agreement = getEContractAgreements(state)[agreementId];
  if (!agreement) {
    return null;
  }
  return getBusinesses(state)[agreement.buyerBusinessId] || null;
};

export const getVendorByEContractAgreementId: (
  agreementId: string,
) => (state: State) => Business | null = agreementId => state => {
  const agreement = getEContractAgreements(state)[agreementId];
  if (!agreement) {
    return null;
  }
  return getBusinesses(state)[agreement.vendorBusinessId] || null;
};
